import { createContext, ReactNode, useContext, useMemo } from 'react';
import {
  FetchBuildingsResult,
  FetchLevelsResult,
  FetchPropertySuitesResult,
  usePropertiesAPI,
  UsePropertiesAPIReturn,
} from 'src/SharedCustomHooks/UsePropertiesAPI/usePropertiesAPI';

interface ContactsDataType {
  isLoading: boolean;
}

interface ContactsActionsType {
  fetchBuildingForProperty: (
    propertyId: string
  ) => Promise<FetchBuildingsResult>;
  fetchLevelsForBuilding: (buildingId: string) => Promise<FetchLevelsResult>;
  fetchPropertySuites: (
    propertyId: string,
    userId: string,
    role: string
  ) => Promise<FetchPropertySuitesResult>;
}

interface ContactsProviderProps {
  children: ReactNode;
}

const DataContext = createContext<ContactsDataType | undefined>(undefined);
const ActionsContext = createContext<ContactsActionsType | undefined>(
  undefined
);

export const ContactsSuiteActionsProvider = ({
  children,
}: ContactsProviderProps) => {
  const propertiesAPI: UsePropertiesAPIReturn = usePropertiesAPI();

  const isLoading: boolean =
    propertiesAPI.isLoadingBuildings ||
    propertiesAPI.isLoadingLevels ||
    propertiesAPI.isLoadingPropertySuites;

  const dataValue = useMemo(
    () => ({ isLoading }),
    [isLoading]
  );

  const actionsValue = useMemo(
    () => ({
      fetchBuildingForProperty: propertiesAPI.fetchBuildingForProperty,
      fetchLevelsForBuilding: propertiesAPI.fetchLevelsForBuilding,
      fetchPropertySuites: propertiesAPI.fetchPropertySuites,
    }),
    [
      propertiesAPI.fetchBuildingForProperty,
      propertiesAPI.fetchLevelsForBuilding,
      propertiesAPI.fetchPropertySuites,
    ]
  );

  return (
    <ActionsContext.Provider value={actionsValue}>
      <DataContext.Provider value={dataValue}>{children}</DataContext.Provider>
    </ActionsContext.Provider>
  );
};

export const useContactsSuiteActionsDataFromContext = (): ContactsDataType => {
  const context: ContactsDataType | undefined = useContext(DataContext);

  if (context === undefined) {
    throw new Error(
      'useContactsDataFromContext must be used within a ContactsProvider'
    );
  }

  return context;
};

export const useContactsSuiteActionsFromContext = (): ContactsActionsType => {
  const context: ContactsActionsType | undefined = useContext(ActionsContext);

  if (context === undefined) {
    throw new Error(
      'useContactsActionsFromContext must be used within a ContactsProvider'
    );
  }

  return context;
};
